import type Fuse from "fuse.js";
import { type FC, type MutableRefObject, useRef } from "react";

import { InputText } from "@moment/ui";

import { useEditableValue } from "../useEditableValue";
import { TableCellValueInputClickMask } from "./TableCellValueInputClickMask";
import { type TableCellValueCommonProps } from "./types";
import { markHighlights } from "./utils";

export interface TableCellValueInputTextProps extends TableCellValueCommonProps {
	highlightIndices?: Fuse.FuseResultMatch["indices"];
	renderAsRowDetail?: boolean;
	cellValueRef?: MutableRefObject<HTMLDivElement>;
}

export const TableCellValueInputText: FC<TableCellValueInputTextProps> = ({
	value: inputValue,
	defaultValue: defaultInputValue,
	highlightIndices,
	onChange,
	renderAsRowDetail,
	cellValueRef,
	isEditable = true,
	header,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const {
		value,
		isControlled,
		isEditing,
		handleEditButtonClick,
		handleBlur,
		handleChange,
		handleKeyDown,
	} = useEditableValue<string>(
		"text",
		isEditable,
		inputValue ?? "",
		defaultInputValue,
		onChange,
		inputRef
	);

	const staticContent = <>{markHighlights(value, highlightIndices)}</>;

	if (!isEditable) {
		return staticContent;
	}

	const input = (
		<InputText
			aria-label={header}
			defaultValue={defaultInputValue}
			value={isControlled ? value : undefined}
			ref={inputRef}
			size="sm"
			fullWidth
			noChrome={!renderAsRowDetail}
			onBlur={handleBlur}
			onKeyDown={handleKeyDown}
			onChange={(e) => handleChange(e.target.value)}
		/>
	);

	if (renderAsRowDetail) {
		return input;
	}

	const editableContent = isEditing ? (
		input
	) : (
		<TableCellValueInputClickMask
			onDoubleClick={handleEditButtonClick}
			isEditing={isEditing}
			cellValueRef={cellValueRef}
		>
			{staticContent}
		</TableCellValueInputClickMask>
	);

	return isEditable ? editableContent : staticContent;
};
